import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Plus } from 'lucide-react';
import { todayInTz, type BudgetDto, type CurrencyCode, type PaymentStateDto } from '@trestly/shared';
import { useBudgets, usePayments, useSetPaymentPaid } from '../api/hooks';
import { usePeriod } from '../period-context';
import { useQuickExpense } from '../quick-expense';
import { ScreenHeader } from '../components/ScreenHeader';
import { EnvelopeSheet } from '../components/EnvelopeSheet';
import { EmptyState, ErrorState } from '../components/EmptyState';
import { Checkbox } from '../components/Toggle';
import { useToast } from '../components/Toast';
import { Button } from '@/components/ui/button';
import { envelopeColor, NO_CATEGORY } from '../lib/envelope';
import { errorText } from '../lib/errors';
import { daysBetween, formatIsoDate } from '../lib/format';
import { currencySign, money, moneyLabel } from '../lib/money';
import { periodPace } from '../lib/period';
import { haptic } from '@/telegram';
import { cn } from '@/lib/utils';
import { t } from '../i18n';

/** Платежи ближе этого срока попадают на главный экран. */
const SOON_DAYS = 7;
/** Сколько платежей показывать на главной, остальные — в разделе. */
const SOON_LIMIT = 3;

/**
 * Главный экран отвечает на два вопроса: сколько можно тратить и что скоро
 * платить. Сверху — итог периода и «в день», ниже ближайшие платежи с галкой,
 * затем конверты строками: тап по строке открывает трату в этот конверт,
 * шеврон — сам конверт.
 */
export function Envelopes(): JSX.Element {
  const { period, todayPeriod, cycleStartDay } = usePeriod();
  const budgets = useBudgets(period);
  const quick = useQuickExpense();
  const [creating, setCreating] = useState(false);
  const todayIso = todayInTz();
  const isCurrent = period === todayPeriod;
  const pace = isCurrent ? periodPace(period, cycleStartDay, todayIso) : null;

  const data = budgets.data;

  return (
    <>
      <ScreenHeader title={t('envelopesPage.title')} />

      {budgets.isPending ? (
        <HomeSkeleton />
      ) : budgets.isError ? (
        <div className="px-4">
          <ErrorState message={errorText(budgets.error)} onRetry={() => void budgets.refetch()} />
        </div>
      ) : data ? (
        <div className="px-4">
          {data.items.length === 0 ? (
            <div className="panel mb-4">
              <EmptyState
                title={t('envelopesPage.emptyTitle')}
                description={t('envelopesPage.emptyDesc')}
                action={<Button onClick={() => setCreating(true)}>{t('envelopesPage.emptyAction')}</Button>}
              />
            </div>
          ) : (
            <Summary
              limitMinor={data.totalLimitMinor}
              spentMinor={data.totalSpentMinor}
              currency={data.baseCurrency}
              daysLeft={pace?.daysLeft ?? null}
              elapsed={pace?.elapsed ?? null}
            />
          )}

          {isCurrent ? <SoonPayments period={period} todayIso={todayIso} /> : null}

          {data.items.length > 0 || data.uncategorizedSpentMinor > 0 ? (
            <section className="mb-5">
              <div className="flex items-baseline justify-between px-1 pb-2">
                <h2 className="section-title">{t('envelopesPage.envelopes')}</h2>
                {pace ? (
                  <span className="flex items-center gap-1.5 text-[12px] text-faint">
                    <span className="inline-block h-2.5 w-[2px] rounded-full bg-foreground/35" aria-hidden />
                    {t('envelopesPage.paceMark')}
                  </span>
                ) : null}
              </div>
              <ul className="panel overflow-hidden" style={{ '--sep': '66px' } as React.CSSProperties}>
                {data.items.map((budget) => (
                  <EnvelopeRow
                    key={budget.categoryId}
                    budget={budget}
                    currency={data.baseCurrency}
                    elapsed={pace?.elapsed ?? null}
                    onAdd={() => quick.open({ categoryId: budget.categoryId })}
                  />
                ))}
                <li style={{ '--c': NO_CATEGORY.color } as React.CSSProperties}>
                  <div className="flex items-center">
                    <button
                      type="button"
                      className="row-press flex min-w-0 flex-1 items-center gap-3 py-3 pl-4 text-left"
                      onClick={() => quick.open({ categoryId: null })}
                      aria-label={t('envelopesPage.noCategoryAdd')}
                    >
                      <span className="env-badge" aria-hidden>
                        ?
                      </span>
                      <span className="min-w-0 flex-1 truncate text-[15.5px] font-medium text-muted-foreground">
                        {NO_CATEGORY.name}
                      </span>
                      <span className="num text-[16px] font-semibold text-muted-foreground">
                        {money(data.uncategorizedSpentMinor, data.baseCurrency)}
                      </span>
                    </button>
                    <Link
                      to="/envelopes/none"
                      aria-label={t('envelopesPage.noCategoryOpen')}
                      className="flex h-16 w-11 shrink-0 items-center justify-center text-faint active:opacity-60"
                      onClick={() => haptic.tap('soft')}
                    >
                      <ChevronRight className="size-[18px]" aria-hidden />
                    </Link>
                  </div>
                </li>
              </ul>
            </section>
          ) : null}

          {data.items.length > 0 ? (
            <button
              type="button"
              className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-card text-accent active:opacity-60"
              onClick={() => {
                haptic.tap();
                setCreating(true);
              }}
            >
              <Plus className="size-[18px]" aria-hidden />
              <span className="text-[15px] font-semibold">{t('envelopesPage.newEnvelope')}</span>
            </button>
          ) : null}

          <EnvelopeSheet
            open={creating}
            budget={null}
            period={period}
            baseCurrency={data.baseCurrency}
            onClose={() => setCreating(false)}
          />
        </div>
      ) : null}
    </>
  );
}

/**
 * Итог периода. Главное число — сколько можно тратить в день, чтобы дожить до
 * конца периода; под ним полоса израсходованного с засечкой «сколько дней
 * прошло» и три метрики.
 */
function Summary({
  limitMinor,
  spentMinor,
  currency,
  daysLeft,
  elapsed,
}: {
  limitMinor: number;
  spentMinor: number;
  currency: CurrencyCode;
  /** null — период не текущий: «в день» и «дней» не имеют смысла. */
  daysLeft: number | null;
  elapsed: number | null;
}): JSX.Element {
  const leftMinor = limitMinor - spentMinor;
  const over = leftMinor < 0;
  const perDay = daysLeft && leftMinor > 0 ? Math.floor(leftMinor / daysLeft) : null;
  const spentShare = limitMinor > 0 ? Math.min(1, spentMinor / limitMinor) : 0;
  // Траты обгоняют время больше чем на 5 п.п. — говорим словами, не только цветом.
  const ahead = elapsed !== null && !over && spentShare - elapsed > 0.05;

  return (
    <div className="hero-card mb-7 px-5 pt-5 pb-5">
      <div className="relative">
        <div className="text-[13px] font-medium text-[var(--hero-ink-2)]">
          {perDay !== null ? t('envelopesPage.perDay') : over ? t('envelopesPage.over') : t('envelopesPage.left')}
        </div>
        <div className={cn('num-hero mt-2 text-[52px] leading-none font-bold', over && 'text-destructive')}>
          {money(perDay ?? leftMinor, currency)}
          <span className="ml-1.5 text-[28px] font-semibold text-[var(--ink-3)]">{currencySign(currency)}</span>
        </div>

        <div className="relative mt-6 h-1.5 overflow-hidden rounded-full bg-[var(--hero-track)]">
          <div
            className="h-full rounded-full transition-[width] duration-500"
            style={{ width: `${Math.round(spentShare * 100)}%`, background: over ? 'var(--danger)' : 'var(--hero-fill)' }}
          />
          {elapsed !== null ? (
            <span
              className="absolute top-0 bottom-0 w-[2px] -translate-x-1/2 rounded-full bg-foreground/45"
              style={{ left: `${Math.round(elapsed * 100)}%` }}
              aria-hidden
            />
          ) : null}
        </div>

        <div className="mt-5 grid grid-cols-3 gap-2 text-[12.5px] text-[var(--hero-ink-2)]">
          <Metric label={over ? t('envelopesPage.overShort') : t('envelopesPage.leftShort')} value={money(leftMinor, currency)} />
          <Metric label={t('envelopesPage.spentShort')} value={money(spentMinor, currency)} />
          {daysLeft !== null ? (
            <Metric label={t('envelopesPage.daysAhead', { n: daysLeft })} value={String(daysLeft)} />
          ) : (
            <Metric label={t('envelopesPage.limitShort')} value={money(limitMinor, currency)} />
          )}
        </div>
        {ahead ? (
          <div className="mt-5 flex items-start gap-2 rounded-2xl bg-[color-mix(in_srgb,var(--warn)_10%,transparent)] px-3.5 py-2.5 text-[13px] leading-snug text-[var(--warn)]">
            {t('envelopesPage.ahead', { spent: Math.round(spentShare * 100), days: Math.round((elapsed ?? 0) * 100) })}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="min-w-0">
      <div className="num truncate text-[17px] font-semibold text-[var(--hero-ink)]">{value}</div>
      <div className="truncate">{label}</div>
    </div>
  );
}

/** Просроченные и ближайшие платежи с галкой: отметить оплату можно прямо с главной. */
function SoonPayments({ period, todayIso }: { period: string; todayIso: string }): JSX.Element | null {
  const payments = usePayments(period);
  const setPaid = useSetPaymentPaid(period);
  const toast = useToast();

  const soon = useMemo(
    () =>
      (payments.data?.items ?? [])
        .filter((item) => item.paidAt === null && daysBetween(todayIso, item.dueDate) < SOON_DAYS)
        .sort((a, b) => a.dueDate.localeCompare(b.dueDate)),
    [payments.data, todayIso],
  );

  if (!payments.data || soon.length === 0) return null;
  const currency = payments.data.baseCurrency;
  const sum = soon.reduce((total, item) => total + item.amountBaseMinor, 0);

  return (
    <section className="mb-7">
      <div className="flex items-baseline justify-between px-1 pb-2">
        <h2 className="section-title">
          {t('envelopesPage.soon')} · <span className="num">{money(sum, currency)}</span>
        </h2>
        <Link
          to="/obligations"
          className="flex items-center gap-0.5 text-[13px] font-semibold text-accent no-underline active:opacity-60"
          onClick={() => haptic.tap('soft')}
        >
          {t('envelopesPage.allPayments')}
          <ChevronRight className="size-4" aria-hidden />
        </Link>
      </div>
      <ul className="panel overflow-hidden" style={{ '--sep': '56px' } as React.CSSProperties}>
        {soon.slice(0, SOON_LIMIT).map((item) => (
          <SoonRow
            key={item.paymentId}
            item={item}
            todayIso={todayIso}
            onPaid={() => {
              if (setPaid.isPending) return;
              setPaid.mutate(
                { id: item.paymentId, paid: true },
                {
                  // Галку легко задеть при прокрутке: отметка — с отменой, как у трат.
                  onSuccess: () =>
                    toast.show({
                      text: t('envelopesPage.paidToast', { name: item.name }),
                      action: {
                        label: t('envelopesPage.undo'),
                        onClick: () => setPaid.mutate({ id: item.paymentId, paid: false }),
                      },
                    }),
                  onError: (error) => {
                    haptic.result('error');
                    toast.show({ text: t('envelopesPage.markFailed', { error: errorText(error) }) });
                  },
                },
              );
            }}
          />
        ))}
        {soon.length > SOON_LIMIT ? (
          <li>
            <Link
              to="/obligations"
              className="row-press flex h-11 items-center justify-center text-[13.5px] font-medium text-muted-foreground no-underline"
            >
              {t('envelopesPage.more', { n: soon.length - SOON_LIMIT })}
            </Link>
          </li>
        ) : null}
      </ul>
    </section>
  );
}

function SoonRow({
  item,
  todayIso,
  onPaid,
}: {
  item: PaymentStateDto;
  todayIso: string;
  onPaid: () => void;
}): JSX.Element {
  const diff = daysBetween(todayIso, item.dueDate);
  const late = diff < 0;
  const when =
    diff === 0
      ? t('envelopesPage.today')
      : diff === 1
        ? t('envelopesPage.tomorrow')
        : late
          ? t('envelopesPage.overdueBy', { n: -diff })
          : formatIsoDate(item.dueDate);
  return (
    <li className="flex min-h-[56px] items-center gap-3 py-2 pr-4 pl-4">
      <span className={cn(late && '[&>button]:border-destructive')}>
        <Checkbox checked={false} onChange={onPaid} label={t('envelopesPage.markPaid', { name: item.name })} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[15px] font-medium">{item.name}</span>
        <span className={cn('block text-[12.5px]', late ? 'text-destructive' : 'text-faint')}>{when}</span>
      </span>
      <span className="num text-[15px] font-semibold whitespace-nowrap">
        {moneyLabel(item.amountMinorActual ?? item.amountMinor, item.currency)}
      </span>
    </li>
  );
}

/**
 * Строка конверта: значок-буква в цвете конверта, остаток справа, полоса
 * остатка с засечкой темпа. Тап — трата в конверт, шеврон — экран конверта.
 */
function EnvelopeRow({
  budget,
  currency,
  elapsed,
  onAdd,
}: {
  budget: BudgetDto;
  currency: CurrencyCode;
  elapsed: number | null;
  onAdd: () => void;
}): JSX.Element {
  const color = envelopeColor(budget.categoryColor, budget.categoryPosition);
  const hasLimit = budget.limitMinor > 0;
  const remaining = budget.remainingMinor;
  const over = hasLimit && remaining < 0;
  const share = hasLimit ? Math.max(0, Math.min(1, remaining / budget.limitMinor)) : 0;
  // Остаток заметно меньше, чем должен быть по темпу, — сумма подсвечивается.
  const behind = hasLimit && !over && elapsed !== null && share < 1 - elapsed - 0.1;

  return (
    <li style={{ '--c': color } as React.CSSProperties}>
      <div className="flex items-center">
        <button
          type="button"
          className="row-press flex min-w-0 flex-1 items-center gap-3 py-3 pl-4 text-left"
          onClick={onAdd}
          aria-label={t('envelopesPage.addTo', { name: budget.categoryName })}
        >
          <span className="env-badge" aria-hidden>
            {budget.categoryName.trim().charAt(0).toUpperCase()}
          </span>
          <span className="min-w-0 flex-1">
            <span className="flex items-baseline justify-between gap-3">
              <span className="truncate text-[15.5px] font-semibold">{budget.categoryName}</span>
              <span
                className={cn(
                  'num shrink-0 text-[16px] font-semibold',
                  over && 'text-destructive',
                  behind && 'text-[var(--warn)]',
                )}
              >
                {hasLimit ? money(remaining, currency) : money(budget.spentMinor, currency)}
              </span>
            </span>
            {hasLimit ? (
              <span className="meter mt-2" aria-hidden>
                <span
                  style={{
                    width: over ? '100%' : `${Math.round(share * 100)}%`,
                    background: over ? 'var(--danger)' : undefined,
                  }}
                />
                {elapsed !== null && !over ? <i style={{ left: `${Math.round((1 - elapsed) * 100)}%` }} /> : null}
              </span>
            ) : null}
            <span className="num mt-1.5 block text-[12.5px] text-muted-foreground">
              {over
                ? t('envelopesPage.tileOver', { limit: money(budget.limitMinor, currency) })
                : hasLimit
                  ? t('envelopesPage.tileLeft', { limit: money(budget.limitMinor, currency) })
                  : t('envelopesPage.tileNoLimit')}
            </span>
          </span>
        </button>
        <Link
          to={`/envelopes/${budget.categoryId}`}
          aria-label={t('envelopesPage.open', { name: budget.categoryName })}
          className="flex h-16 w-11 shrink-0 items-center justify-center text-faint active:opacity-60"
          onClick={() => haptic.tap('soft')}
        >
          <ChevronRight className="size-[18px]" aria-hidden />
        </Link>
      </div>
    </li>
  );
}

function HomeSkeleton(): JSX.Element {
  return (
    <div className="px-4" role="status" aria-label={t('envelopesPage.loading')}>
      <div className="mb-5 h-[196px] animate-pulse rounded-[26px] bg-muted" />
      <div className="panel overflow-hidden">
        {Array.from({ length: 5 }, (_, index) => (
          <div key={index} className="flex items-center gap-3 px-4 py-3.5">
            <div className="size-10 animate-pulse rounded-xl bg-muted" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-1/2 animate-pulse rounded bg-muted" />
              <div className="h-1.5 animate-pulse rounded bg-muted" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}